import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBookings } from '../../../../hooks/accommodation';
import { useAppSelector } from '../../../../hooks/useRedux';
import { selectAccommodationProperty } from '../../../../store/accommodation';
import { BookingStatusBadge } from './BookingStatusBadge';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function nightsBetween(checkIn: string, checkOut: string) {
  return Math.round(
    (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000,
  );
}

export const BookingDetailPageHeader: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backRoute = searchParams.get('back') || '/accommodation/bookings';

  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookings = [] } = useBookings(property?.id);
  const booking = bookings.find((b) => b.id === bookingId) ?? null;

  if (!booking) return null;

  const guestName = booking.client
    ? `${booking.client.first_name} ${booking.client.last_name}`
    : '—';
  const nights = nightsBetween(booking.check_in, booking.check_out);

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => navigate(backRoute)}
        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
      >
        <ArrowLeft size={16} />
      </button>
      <div className="flex-1">
        <h3 className="text-base font-semibold text-gray-900">{guestName}</h3>
        <p className="text-xs text-gray-400 font-medium mt-0.5">
          {formatDate(booking.check_in)} — {formatDate(booking.check_out)} ·{' '}
          {nights} night{nights !== 1 ? 's' : ''}
        </p>
      </div>
      <BookingStatusBadge status={booking.status} />
    </div>
  );
};
